import { useEventListener } from "@vueuse/core";

type FileDragEvent = DragEvent & { dataTransfer: DataTransfer };

/** Whether a drag carries files; text or links dragged across the page are not uploads. */
export const carriesFiles = (event: DragEvent): event is FileDragEvent =>
  event.dataTransfer?.types.includes("Files") ?? false;

/**
 * Keeps a file dropped outside every drop zone from making the browser open it in place of the
 * app, which would throw away whatever the page held (a draft, a chat conversation).
 *
 * Call it once, from App. Drop zones claim a drag by cancelling its dragover before the event
 * bubbles up to window, so they keep working. A native file input accepts a drop without
 * cancelling anything, so a drag over one is left to the browser.
 */
export function useStrayFileDropGuard(): void {
  useEventListener(window, "dragover", (event: DragEvent) => {
    if (!isStrayFileDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "none";
  });
  useEventListener(window, "drop", (event: DragEvent) => {
    if (isStrayFileDrag(event)) event.preventDefault();
  });
}

const isStrayFileDrag = (event: DragEvent): event is FileDragEvent =>
  carriesFiles(event) && !event.defaultPrevented && !isFileInput(event.target);

const isFileInput = (target: EventTarget | null): boolean =>
  target instanceof HTMLInputElement && target.type === "file";
